/**
 * NOTAMs on the map (migration plan H8; sign-off 04-008, 04-009 and the "Naips Towers" part of 04-015;
 * docs/specs/map.md §14). It turns the NOTAMs of NAIPS briefings (`parseNaipsBriefing`) into map features: a dashed
 * circle per NOTAM in the Diurnal tool's category colours, or a point per stated obstacle position, with the words
 * that go with each colour. It also says what it could not place, hides expired NOTAMs and those wholly above the
 * ceiling, keeps the offline copy's rules, and lists the NOTAM obstacles a hazard map takes.
 *
 * Diurnal sources: `aircraft-map.js` `NOTAM_STYLE` 11138, `_notamCategoriseFromText` 11686,
 * `_notamBuildNaipsRecord` 11601 (placement and the 2 NM / 5 NM defaults), `_notamExtractObstaclePositions` 11708,
 * `_notamShouldShow` 12301, `renderNotamLayer` 12319, `_saveNotamCache` / `_loadNotamCache` 12697–12739, and the
 * hazard step 14303 (inventory 04 §3, §4.3 step 6).
 */
import { AU_AERODROMES, type Aerodrome, type AerodromeLookup } from './aerodromes.js';
import type { IdentifyRow } from './map-kml.js';
import {
  blockFeatures,
  featurePoints,
  plannedLineFeatures,
  type FeatureStyle,
  type LayerResult,
  type LegendEntry,
  type MapColour,
  type MapFeature,
} from './map-layers.js';
import type { Block, PlannedLine, Project } from './model.js';
import type {
  NaipsMetReport,
  NaipsNotam,
  NaipsParsedBriefing,
  NotamHeight,
  NotamPosition,
} from './naips-briefing.js';
import type { NaipsAreaBatch } from './naips-areas.js';
import type { LatLng } from './planning.js';

/**
 * The points the automatic NAIPS area choice ranks from (§14.2): the corners of the project's block boundaries, or
 * the ends of its planned lines when no block has a boundary; none when nothing has a map position.
 */
export function naipsProjectPoints(
  blocks: readonly Block[],
  plan: readonly PlannedLine[],
  project: Pick<Project, 'epsg'>,
): LatLng[] {
  const corners = blockFeatures(blocks, project).features.flatMap(featurePoints);
  return corners.length > 0
    ? corners
    : plannedLineFeatures(plan, blocks, project).features.flatMap(featurePoints);
}

// ------------------------------------------------------------------------------------------------ categories

/** The Diurnal tool's NOTAM categories (`NOTAM_STYLE`). `rpd` is a restricted, danger or prohibited area NOTAM whose
 * text names no designator letter. */
export type NotamCategory =
  'rpd_r' | 'rpd_d' | 'rpd_p' | 'rpd' | 'rwy' | 'nav' | 'airspace' | 'obs' | 'gnss' | 'other';

export interface NotamCategoryDef {
  readonly id: NotamCategory;
  /** The word that always goes with the colour (legend, tooltip, identify). */
  readonly word: string;
  /** The tool's short badge: R, D, P, RWY, NAV, AS, OBS, GNSS, NOTAM. */
  readonly short: string;
  /** The tool's colour, kept as the `notam-*` tokens (brand/tokens). */
  readonly colour: MapColour;
  /** The tool's circle fill opacity. */
  readonly fill: number;
}

/** In the order of the tool's layer panel. */
export const NOTAM_CATEGORIES: readonly NotamCategoryDef[] = [
  { id: 'rpd_r', word: 'Restricted area', short: 'R', colour: 'notam-rpd-r', fill: 0.18 },
  { id: 'rpd_d', word: 'Danger area', short: 'D', colour: 'notam-rpd-d', fill: 0.16 },
  { id: 'rpd_p', word: 'Prohibited area', short: 'P', colour: 'notam-rpd-p', fill: 0.22 },
  {
    id: 'rpd',
    word: 'Restricted, danger or prohibited area',
    short: 'R/P/D',
    colour: 'notam-rpd',
    fill: 0.18,
  },
  { id: 'rwy', word: 'Runway or aerodrome', short: 'RWY', colour: 'notam-rwy', fill: 0.18 },
  { id: 'nav', word: 'Navigation aid', short: 'NAV', colour: 'notam-nav', fill: 0.12 },
  { id: 'airspace', word: 'Airspace', short: 'AS', colour: 'notam-airspace', fill: 0.12 },
  { id: 'obs', word: 'Obstacle', short: 'OBS', colour: 'notam-obs', fill: 0.18 },
  { id: 'gnss', word: 'GNSS or GPS', short: 'GNSS', colour: 'notam-gnss', fill: 0.12 },
  { id: 'other', word: 'Other NOTAM', short: 'NOTAM', colour: 'notam-other', fill: 0.18 },
];

const CATEGORY = new Map(NOTAM_CATEGORIES.map((c) => [c.id, c]));
export const notamCategoryDef = (id: NotamCategory): NotamCategoryDef => CATEGORY.get(id)!;

/**
 * A NAIPS NOTAM's category from its text (NAIPS domestic NOTAMs carry no Q-code): the first rule that matches, in the
 * Diurnal tool's order. Two changes from the tool: words are matched whole where the tool matched inside other words
 * (its "ILS" made "AD CTC DETAILS AMD" a navigation aid), and a designator's letter splits restricted (R), danger
 * (D) and prohibited (P) areas, which the tool's colours already distinguish.
 */
export function notamCategory(text: string): NotamCategory {
  const t = text.toUpperCase();
  if (/\bOBST|\bCRANES?\b|\bMASTS?\b|\bTOWERS?\b|WIND\s*FARM|TURBINE/.test(t)) return 'obs';
  if (/\b(GNSS|GPS|RAIM)\b/.test(t)) return 'gnss';
  if (/\b(NAVAIDS?|VOR|DME|NDB|ILS|TACAN|GBAS)\b/.test(t)) return 'nav';
  if (
    /\b(RWYS?|RUNWAYS?|TWYS?|TAXIWAYS?|APRONS?|FUEL|PAPI|VASIS|LIGHTING)\b|\bAD\s+CLSD\b|\bAERODROME\s+CLOSED\b/.test(
      t,
    )
  )
    return 'rwy';
  if (/\bUAS?\b|\bUNMANNED\b|\bMULTI-?ROTOR\b|\bDRONES?\b/.test(t)) return 'airspace';
  const designator = /\b([RDP])\d+[A-Z]{0,2}\b/.exec(t);
  if (designator) return designator[1] === 'R' ? 'rpd_r' : designator[1] === 'D' ? 'rpd_d' : 'rpd_p';
  if (/\b(RESTRICTED|DANGER|PROHIBITED)\s+AREA\b/.test(t)) return 'rpd';
  if (/RESTRICTED|DANGER\s+AREA|PROHIBITED|AIRSPACE/.test(t)) return 'airspace';
  return 'other';
}

// ------------------------------------------------------------------------------------------------- placement

/** The Diurnal tool's default circle (decision 222): 2 NM at an aerodrome, 5 NM for an FIR-wide NOTAM. */
export const NOTAM_DEFAULT_RADIUS_NM = 2;
export const NOTAM_FIR_DEFAULT_RADIUS_NM = 5;

export interface NotamShape {
  readonly centre: LatLng;
  readonly radiusNm: number;
  /** Where the centre came from: a position in the text, or an aerodrome's (named in the text or the header). */
  readonly centreFrom: 'position' | 'aerodrome';
  readonly aerodrome: Aerodrome | null;
  /** The radius is the text's own (else the default circle). */
  readonly radiusStated: boolean;
}

export type NotamPlacement =
  | {
      readonly kind: 'circles';
      readonly circles: readonly NotamShape[];
      /** Position or radius not stated in the text (decision 222: marked as estimated). */
      readonly estimated: boolean;
      /** How it was placed, in words. */
      readonly how: string;
    }
  | {
      /** An area the text bounds by a list of positions ("OPR WI AREA BOUNDED BY …", "OPR BTN FLW PSN:"). */
      readonly kind: 'area';
      readonly ring: readonly LatLng[];
      readonly how: string;
    }
  | { readonly kind: 'obstacles'; readonly obstacles: readonly NotamPosition[]; readonly how: string }
  | { readonly kind: 'unplaced'; readonly reason: string };

const NM_M = 1852;
const aerodromeWords = (a: Aerodrome): string => `${a.name} (${a.code}), OurAirports position`;

/** The phrases that open a list of positions bounding an area, as NAIPS prints them. */
const AREA_OPENING = /\b(?:AREA BOUNDED BY(?: THE FLW PSN)?|(?:WI|BTN) (?:THE )?FLW PSN)\s*:?/;
const LISTED =
  /^\s*(?:[-/,]\s*)?(?:PSN\s+)?(\d{2})(\d{2})(\d{2}(?:\.\d+)?)([NS])\s*(\d{3})(\d{2})(\d{2}(?:\.\d+)?)([EW])/;

/**
 * The area a NOTAM bounds by a list of positions: the positions straight after the opening phrase, separated by
 * spaces, line breaks, "-", "/" or ",", read until the first thing that is not a position (a closing repeat of the
 * first position dropped). At least three positions, else none.
 */
export function notamStatedArea(text: string): LatLng[] | null {
  const open = AREA_OPENING.exec(text);
  if (!open) return null;
  let rest = text.slice(open.index + open[0].length).replace(/\s+/g, ' ');
  const ring: LatLng[] = [];
  for (let m = LISTED.exec(rest); m; m = LISTED.exec(rest)) {
    const lat = (Number(m[1]) + Number(m[2]) / 60 + Number(m[3]) / 3600) * (m[4] === 'S' ? -1 : 1);
    const lng = (Number(m[5]) + Number(m[6]) / 60 + Number(m[7]) / 3600) * (m[8] === 'W' ? -1 : 1);
    ring.push({ lat, lng });
    rest = rest.slice(m[0].length);
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && ring.length > 1 && first.lat === last.lat && first.lng === last.lng) ring.pop();
  return ring.length >= 3 ? ring : null;
}

/** "WI 15NM OF AD": a radius round the NOTAM's own aerodrome (the tool read any "WI n NM", `_notamBuildNaipsRecord`). */
const RADIUS_OF_AD = /\bWI (\d+(?:\.\d+)?) ?NM OF (?:THE )?(?:AD|ARP)\b/;

/**
 * Where a NOTAM goes on the map:
 * 1. an obstacle NOTAM with stated positions: a point at each position (the tool drew tower icons and no circle; it
 *    found a position only on a line that also named the obstacle, so it missed most real ones);
 * 2. an area the text bounds by a list of positions: that area (the tool drew a default circle at its centre);
 * 3. the circles the text states ("WI 2NM RADIUS OF PSN …", or "… OF MEEKATHARRA AD (YMEK)" at that aerodrome), and
 *    "WI 15NM OF AD" round the NOTAM's own aerodrome;
 * 4. a stated position without a radius: the default circle around the first position (radius estimated);
 * 5. no position (decision 222): at the aerodrome with the default circle, marked estimated. The aerodrome is the
 *    location header's, else the first aerodrome code in the text, which is how an FIR-wide NOTAM is placed (as the
 *    tool did);
 * 6. otherwise not placed, and listed with the reason.
 */
export function placeNotam(n: NaipsNotam, aerodromes: AerodromeLookup = AU_AERODROMES): NotamPlacement {
  const defaultNm = n.fir ? NOTAM_FIR_DEFAULT_RADIUS_NM : NOTAM_DEFAULT_RADIUS_NM;
  const category = notamCategory(n.text);
  if (category === 'obs' && n.positions.length > 0)
    return { kind: 'obstacles', obstacles: n.positions, how: 'Stated in the NOTAM' };
  const area = notamStatedArea(n.text);
  if (area)
    return {
      kind: 'area',
      ring: area,
      how: `Stated in the NOTAM: an area bounded by ${area.length} positions`,
    };
  const home = n.locationCodes.map((c) => aerodromes.get(c)).find((x) => x !== undefined);
  const stated: NotamShape[] = [];
  for (const c of n.circles) {
    if (c.centre) {
      stated.push({
        centre: { lat: c.centre.lat, lng: c.centre.lon },
        radiusNm: c.radiusNm,
        centreFrom: 'position',
        aerodrome: null,
        radiusStated: true,
      });
    } else if (c.aerodrome) {
      const a = aerodromes.get(c.aerodrome);
      if (a)
        stated.push({
          centre: { lat: a.lat, lng: a.lon },
          radiusNm: c.radiusNm,
          centreFrom: 'aerodrome',
          aerodrome: a,
          radiusStated: true,
        });
    }
  }
  const ofAd = RADIUS_OF_AD.exec(n.text);
  if (stated.length === 0 && ofAd && home)
    stated.push({
      centre: { lat: home.lat, lng: home.lon },
      radiusNm: Number(ofAd[1]),
      centreFrom: 'aerodrome',
      aerodrome: home,
      radiusStated: true,
    });
  if (stated.length > 0) {
    const at = stated[0]!.aerodrome;
    return {
      kind: 'circles',
      circles: stated,
      estimated: false,
      how: at
        ? `Stated in the NOTAM: ${stated[0]!.radiusNm} NM around ${aerodromeWords(at)}`
        : `Stated in the NOTAM: ${stated.map((s) => `${s.radiusNm} NM`).join(', ')} circle`,
    };
  }
  const first = n.positions[0];
  if (first) {
    return {
      kind: 'circles',
      circles: [
        {
          centre: { lat: first.lat, lng: first.lon },
          radiusNm: defaultNm,
          centreFrom: 'position',
          aerodrome: null,
          radiusStated: false,
        },
      ],
      estimated: true,
      how: `Estimated: position stated, radius not; drawn with the default ${defaultNm} NM circle`,
    };
  }
  const named = /\b(Y[A-Z]{3})\b/.exec(n.text)?.[1];
  const a = home ?? (named ? aerodromes.get(named) : undefined);
  if (a) {
    return {
      kind: 'circles',
      circles: [
        {
          centre: { lat: a.lat, lng: a.lon },
          radiusNm: defaultNm,
          centreFrom: 'aerodrome',
          aerodrome: a,
          radiusStated: false,
        },
      ],
      estimated: true,
      how: `Estimated: no position in the NOTAM; placed at ${aerodromeWords(a)}, with the default ${defaultNm} NM circle`,
    };
  }
  const designator = /\b([RDP]\d+[A-Z]*)\b/.exec(n.text)?.[1];
  return {
    kind: 'unplaced',
    reason: n.fir
      ? 'FIR-wide, with no position and no aerodrome Teslyn knows in its text'
      : category.startsWith('rpd') && designator
        ? `${designator}: no position in the NOTAM; the area's shape comes from the Airspace (OpenAIP) layer, which does not show it here`
        : `no position, and ${n.locationCodes.join('/')} is not an aerodrome in the aerodrome list`,
  };
}

/**
 * The area designators a NOTAM names in its first line: "R155AB ACT" → R155A, R155B (and R155AB); "R156 ACT" → R156.
 * Only the first line, where NAIPS puts the area ("R156 ACT (RA2) DUE MIL FLYING"), so a runway or a frequency later
 * in the text is never read as an area. Military M areas too (decision 466, "M166 ACT DUE MIL FLYING"), but only on a
 * line that says ACT, so a taxiway M1 is never an area.
 */
export function notamDesignators(n: Pick<NaipsNotam, 'text'>): string[] {
  const first = (n.text.split('\n')[0] ?? '').toUpperCase();
  const pattern = /\bACT(IVE|IVATED)?\b/.test(first)
    ? /\b([RDPM])(\d{1,4})([A-Z]{0,4})\b/g
    : /\b([RDP])(\d{1,4})([A-Z]{0,4})\b/g;
  const out = new Set<string>();
  for (const m of first.matchAll(pattern)) {
    const base = `${m[1]}${m[2]}`;
    const letters = m[3] ?? '';
    out.add(base + letters);
    if (letters.length > 1) for (const l of letters) out.add(base + l);
  }
  return [...out];
}

/** A circle as a ring of `steps` points on the sphere (mean earth radius), for any map engine. */
export function circleRing(centre: LatLng, radiusNm: number, steps = 72): LatLng[] {
  const R = 6_371_008.8;
  const d = (radiusNm * NM_M) / R;
  const lat1 = (centre.lat * Math.PI) / 180;
  const lng1 = (centre.lng * Math.PI) / 180;
  const out: LatLng[] = [];
  for (let i = 0; i < steps; i++) {
    const brg = (2 * Math.PI * i) / steps;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brg));
    const lng2 =
      lng1 +
      Math.atan2(Math.sin(brg) * Math.sin(d) * Math.cos(lat1), Math.cos(d) - Math.sin(lat1) * Math.sin(lat2));
    out.push({ lat: (lat2 * 180) / Math.PI, lng: (lng2 * 180) / Math.PI });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- time, height

export type NotamTimeState = 'in_force' | 'not_yet' | 'expired';

/**
 * Expired when a firm end has passed. An estimated end ("EST") may already be past: such a NOTAM stays in force
 * until it is cancelled or replaced, so it is kept (parser rule, issue #22). A NOTAM that starts later in the
 * briefing's period is "not yet" and still drawn, as in the tool.
 */
export function notamTimeState(n: NaipsNotam, nowMs: number): NotamTimeState {
  if (n.validTo && !n.endEstimated && Date.parse(n.validTo) <= nowMs) return 'expired';
  if (n.validFrom && Date.parse(n.validFrom) > nowMs) return 'not_yet';
  return 'in_force';
}

/** The tool's ceiling (`CONFIG.notamCeilingFt`): "Hide NOTAMs above 10,000 ft AMSL", on by default. */
export const NOTAM_CEILING_FT = 10_000;

/**
 * Wholly above the ceiling: the NOTAM's stated lower limit is above it (a flight level counts in hundreds of feet,
 * AGL counts as AMSL as the tool did). A NOTAM with no stated limits, or one from the surface, is never hidden.
 * The Diurnal tool hid on the upper limit instead (a surface to FL180 restricted area disappeared); which rule MB
 * wants is open (spec §14.5).
 */
export function notamAboveCeiling(
  n: Pick<NaipsNotam, 'lower'>,
  ceilingFt: number = NOTAM_CEILING_FT,
): boolean {
  const lower: NotamHeight | null = n.lower;
  return lower !== null && lower.ft !== null && lower.ft > ceilingFt;
}

// ------------------------------------------------------------------------------------------------- briefings

/** One briefing as the map keeps it: the proxy's text and when NAIPS was asked. */
export interface NotamBriefing {
  readonly text: string;
  readonly fetchedAt: string;
  readonly parsed: NaipsParsedBriefing;
}

const notamKey = (n: NaipsNotam): string => `${n.locationCodes.join('/')} ${n.id}`;

/** The NOTAMs of several briefings, each once (a NOTAM of two areas, or of an area and an aerodrome, appears twice). */
export function mergeNotams(briefings: readonly { readonly parsed: NaipsParsedBriefing }[]): NaipsNotam[] {
  const seen = new Map<string, NaipsNotam>();
  for (const b of briefings)
    for (const n of b.parsed.notams) if (!seen.has(notamKey(n))) seen.set(notamKey(n), n);
  return [...seen.values()];
}

/** The TAF and METAR reports of location briefings (decision 217), each once. */
export function mergeMet(briefings: readonly { readonly parsed: NaipsParsedBriefing }[]): NaipsMetReport[] {
  const seen = new Map<string, NaipsMetReport>();
  for (const b of briefings) for (const m of b.parsed.met) if (!seen.has(m.text)) seen.set(m.text, m);
  return [...seen.values()];
}

// -------------------------------------------------------------------------------------------------- features

export interface NotamFilter {
  /** Categories switched off in the layer panel. */
  readonly hidden: readonly NotamCategory[];
  /** "Hide NOTAMs above 10,000 ft AMSL". */
  readonly ceiling: boolean;
}

export const DEFAULT_NOTAM_FILTER: NotamFilter = { hidden: [], ceiling: true };

export interface NotamCounts {
  /** NOTAMs in the briefings, each once. */
  readonly total: number;
  readonly drawn: number;
  /** Drawn with an estimated position or radius (decision 222). */
  readonly estimated: number;
  readonly unplaced: number;
  readonly expired: number;
  readonly hiddenByCategory: number;
  readonly aboveCeiling: number;
}

export interface NotamLayer extends LayerResult {
  readonly counts: NotamCounts;
  /** A legend row for each category drawn, plus the estimated outline when one is drawn. */
  readonly legend: LegendEntry[];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "19 Oct 2026 07:07 UTC". */
export function utcText(iso: string): string {
  const d = new Date(iso);
  const p = (v: number): string => String(v).padStart(2, '0');
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
}

/** The validity in words: "19 Oct 2026 01:37 UTC to 2 Nov 2026 01:30 UTC (end estimated)", "… permanent". */
export function notamValidityText(n: NaipsNotam): string {
  if (!n.validFrom) return 'Not stated';
  const from = utcText(n.validFrom);
  if (n.permanent) return `From ${from}, permanent`;
  if (!n.validTo) return `From ${from}`;
  return `${from} to ${utcText(n.validTo)}${n.endEstimated ? ' (end estimated: in force until cancelled)' : ''}`;
}

const oneLine = (text: string, max = 200): string => {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
};

const style = (
  colour: MapColour,
  stroke: FeatureStyle['stroke'],
  dashed: boolean,
  fill: number,
): FeatureStyle => ({
  colour,
  stroke,
  dashed,
  fill,
});

function heightText(h: NotamHeight | null): string | null {
  return h ? h.text : null;
}

function identifyRows(
  n: NaipsNotam,
  cat: NotamCategoryDef,
  where: string,
  state: NotamTimeState,
  fetchedAt: string | null,
): IdentifyRow[] {
  return [
    { label: 'NOTAM', value: n.replaces ? `${n.id} (replaces ${n.replaces})` : n.id },
    { label: 'Location', value: `${n.location} (${n.locationCodes.join('/')})` },
    { label: 'Category', value: cat.word },
    {
      label: 'In force',
      value: `${state === 'not_yet' ? 'Not yet: ' : ''}${notamValidityText(n)}`,
    },
    ...(n.schedule ? [{ label: 'Schedule', value: n.schedule }] : []),
    ...(heightText(n.lower) ? [{ label: 'Lower limit', value: heightText(n.lower)! }] : []),
    ...(heightText(n.upper) ? [{ label: 'Upper limit', value: heightText(n.upper)! }] : []),
    { label: 'Position', value: where },
    { label: 'Text', value: n.text },
    {
      label: 'Source',
      value: `NAIPS (Airservices Australia)${fetchedAt ? `, fetched ${utcText(fetchedAt)}` : ''}`,
    },
  ];
}

/**
 * The NOTAM layer: every NOTAM in force (or starting later in the briefing's period) that its category and the
 * ceiling allow, placed by `placeNotam`. Circles are dashed in the category colour with the tool's fill; an estimated
 * one is drawn thin and says "estimated" in its tooltip and identify rows. Stated obstacle positions are points in the
 * obstacle colour. NOTAMs that cannot be placed are listed under "Not drawn" with the reason; expired NOTAMs are
 * counted, never drawn.
 */
export function notamFeatures(
  notams: readonly NaipsNotam[],
  opts: {
    readonly nowMs: number;
    readonly filter?: NotamFilter;
    readonly aerodromes?: AerodromeLookup;
    readonly fetchedAt?: string | null;
    /** R, D and P areas the Airspace (OpenAIP) layer draws: their NOTAMs restyle it (04-010), not "Not drawn". */
    readonly airspaceDrawn?: ReadonlySet<string>;
  },
): NotamLayer {
  const filter = opts.filter ?? DEFAULT_NOTAM_FILTER;
  const hidden = new Set(filter.hidden);
  const features: MapFeature[] = [];
  const notDrawn: string[] = [];
  const drawnCats = new Set<NotamCategory>();
  let drawn = 0;
  let estimated = 0;
  let unplaced = 0;
  let expired = 0;
  let hiddenByCategory = 0;
  let aboveCeiling = 0;
  for (const n of notams) {
    const state = notamTimeState(n, opts.nowMs);
    if (state === 'expired') {
      expired += 1;
      continue;
    }
    const cat = notamCategoryDef(notamCategory(n.text));
    if (hidden.has(cat.id)) {
      hiddenByCategory += 1;
      continue;
    }
    if (filter.ceiling && notamAboveCeiling(n)) {
      aboveCeiling += 1;
      continue;
    }
    const placed = placeNotam(n, opts.aerodromes);
    const key = `notam:${notamKey(n)}`;
    const soon = state === 'not_yet' && n.validFrom ? ` · starts ${utcText(n.validFrom)}` : '';
    if (placed.kind === 'unplaced') {
      if (opts.airspaceDrawn && notamDesignators(n).some((d) => opts.airspaceDrawn!.has(d))) continue;
      unplaced += 1;
      notDrawn.push(`NOTAM ${n.id} (${n.location}): ${placed.reason}`);
      continue;
    }
    drawn += 1;
    drawnCats.add(cat.id);
    if (placed.kind === 'obstacles') {
      placed.obstacles.forEach((o, i) => {
        const label = obstacleLabel(o, n);
        features.push({
          id: `${key}:o${i}`,
          layer: 'notams',
          geometry: { type: 'point', at: { lat: o.lat, lng: o.lon } },
          style: style(cat.colour, 'normal', false, 0.9),
          tooltip: `${n.id} · ${cat.word} · ${label}${soon}`,
          identify: identifyRows(
            n,
            cat,
            `${placed.how}: ${o.lat.toFixed(6)}, ${o.lon.toFixed(6)}${o.height ? `, ${o.height.text}` : ''}`,
            state,
            opts.fetchedAt ?? null,
          ),
        });
      });
      continue;
    }
    if (placed.kind === 'area') {
      features.push({
        id: `${key}:a0`,
        layer: 'notams',
        geometry: { type: 'polygon', rings: [[...placed.ring]] },
        style: style(cat.colour, 'normal', true, cat.fill),
        tooltip: `${n.id} · ${cat.word}${soon} · ${oneLine(n.text)}`,
        identify: identifyRows(n, cat, placed.how, state, opts.fetchedAt ?? null),
      });
      continue;
    }
    if (placed.estimated) estimated += 1;
    placed.circles.forEach((c, i) => {
      features.push({
        id: `${key}:c${i}`,
        layer: 'notams',
        geometry: { type: 'polygon', rings: [circleRing(c.centre, c.radiusNm)] },
        style: style(cat.colour, placed.estimated ? 'thin' : 'normal', true, cat.fill),
        tooltip: `${n.id} · ${cat.word}${placed.estimated ? ' · estimated position' : ''}${soon} · ${oneLine(n.text)}`,
        identify: identifyRows(n, cat, placed.how, state, opts.fetchedAt ?? null),
      });
    });
  }
  const legend: LegendEntry[] = NOTAM_CATEGORIES.filter((c) => drawnCats.has(c.id)).map((c) => ({
    colour: c.colour,
    stroke: 'normal',
    dashed: c.id !== 'obs',
    word: `NOTAM: ${c.word}`,
  }));
  if (estimated > 0)
    legend.push({
      colour: 'ink-muted',
      stroke: 'thin',
      dashed: true,
      word: 'NOTAM: estimated position (thin)',
    });
  return {
    features,
    notDrawn,
    counts: { total: notams.length, drawn, estimated, unplaced, expired, hiddenByCategory, aboveCeiling },
    legend,
  };
}

/** An obstacle's words: its line without the position (else the NOTAM's first line), with the height stated. */
function obstacleLabel(o: NotamPosition, n: NaipsNotam): string {
  const words = o.line.replace(/\s*PSN\s.*$/i, '').trim() || n.text.split('\n')[0]!.trim() || 'Obstacle';
  const squash = (t: string): string => t.replace(/\s+/g, '');
  return o.height && !squash(words).includes(squash(o.height.text)) ? `${words}, ${o.height.text}` : words;
}

// -------------------------------------------------------------------------------------------------- hazards

/** The hazard map folder for NOTAM obstacles, as the Diurnal tool names it (`buildHazardKML` 13817). */
export const NAIPS_TOWERS_FOLDER = 'Naips Towers';

export interface NotamObstacle {
  readonly lat: number;
  readonly lon: number;
  readonly label: string;
  readonly notamId: string;
  readonly height: NotamHeight | null;
}

/**
 * The obstacle positions a generated hazard map puts in its "Naips Towers" folder (decision 84: the hazard pipeline as
 * today; inventory 04 §4.3 step 6): every stated position of an obstacle NOTAM that has not expired, each once (to 5
 * decimals, as the tool).
 */
export function notamObstacles(notams: readonly NaipsNotam[], nowMs: number): NotamObstacle[] {
  const seen = new Set<string>();
  const out: NotamObstacle[] = [];
  for (const n of notams) {
    if (notamTimeState(n, nowMs) === 'expired' || notamCategory(n.text) !== 'obs') continue;
    for (const p of n.positions) {
      const k = `${p.lat.toFixed(5)},${p.lon.toFixed(5)}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ lat: p.lat, lon: p.lon, label: obstacleLabel(p, n), notamId: n.id, height: p.height });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- offline copy

/** NAIPS is asked at most hourly (decision 215, the Diurnal tool's `notamHourlyInterval`). */
export const NOTAM_REFRESH_MS = 3_600_000;

/** The laptop's copy of the last briefings for a project, so the NOTAMs show offline and after a restart. */
export interface NotamCache {
  readonly version: 1;
  /** Which requests the copy answers (`notamBatchesKey`). */
  readonly key: string;
  readonly briefings: ReadonlyArray<{
    readonly areas: readonly string[];
    readonly headOffice: boolean;
    readonly text: string;
    readonly fetchedAt: string;
  }>;
}

export const notamBatchesKey = (batches: readonly NaipsAreaBatch[]): string =>
  batches.map((b) => `${b.areas.join(',')}${b.headOffice ? '+HO' : ''}`).join(';');

/** A stored copy made safe; null when unreadable (the map then asks NAIPS again). */
export function readNotamCache(raw: unknown): NotamCache | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (r.version !== 1 || typeof r.key !== 'string' || !Array.isArray(r.briefings)) return null;
  const briefings: NotamCache['briefings'][number][] = [];
  for (const b of r.briefings as unknown[]) {
    if (typeof b !== 'object' || b === null) return null;
    const x = b as Record<string, unknown>;
    if (
      !Array.isArray(x.areas) ||
      !x.areas.every((a) => typeof a === 'string') ||
      typeof x.headOffice !== 'boolean' ||
      typeof x.text !== 'string' ||
      typeof x.fetchedAt !== 'string' ||
      !Number.isFinite(Date.parse(x.fetchedAt))
    )
      return null;
    briefings.push({
      areas: x.areas as string[],
      headOffice: x.headOffice,
      text: x.text,
      fetchedAt: x.fetchedAt,
    });
  }
  return { version: 1, key: r.key, briefings };
}

/** The oldest fetch time of a copy (the age the map shows). */
export function notamCacheFetchedAt(cache: NotamCache): string | null {
  const times = cache.briefings.map((b) => Date.parse(b.fetchedAt));
  return times.length ? new Date(Math.min(...times)).toISOString() : null;
}

/** Ask NAIPS again: no copy, a copy for other areas, or a copy older than an hour. */
export function notamRefreshDue(
  cache: NotamCache | null,
  batches: readonly NaipsAreaBatch[],
  nowMs: number,
): boolean {
  if (batches.length === 0) return false;
  if (!cache || cache.key !== notamBatchesKey(batches)) return true;
  const at = notamCacheFetchedAt(cache);
  return at === null || nowMs - Date.parse(at) >= NOTAM_REFRESH_MS;
}

/**
 * The NAIPS dot's tooltip on the map's status ribbon (R102): when NAIPS last answered (the copy's oldest briefing, as
 * `notamRefreshDue` counts), and when it is asked again while the copy is under an hour old (null after: within the
 * minute, at the map's next check).
 */
export function notamAskTimes(
  cache: NotamCache | null,
  nowMs: number,
): { readonly lastAt: string | null; readonly nextAt: string | null } {
  const at = cache ? notamCacheFetchedAt(cache) : null;
  if (at === null) return { lastAt: null, nextAt: null };
  const next = Date.parse(at) + NOTAM_REFRESH_MS;
  return { lastAt: at, nextAt: next > nowMs ? new Date(next).toISOString() : null };
}

/** "just now", "20 min ago", "2 h 5 min ago", "3 days ago". */
export function agoText(at: string, nowMs: number): string {
  const mins = Math.max(0, Math.floor((nowMs - Date.parse(at)) / 60_000));
  return mins < 1
    ? 'just now'
    : mins < 60
      ? `${mins} min ago`
      : mins < 48 * 60
        ? `${Math.floor(mins / 60)} h ${mins % 60} min ago`
        : `${Math.floor(mins / 1440)} days ago`;
}

/** "Fetched 19 Oct 2026 07:07 UTC, 2 h 5 min ago". */
export function notamAgeText(fetchedAt: string, nowMs: number): string {
  return `Fetched ${utcText(fetchedAt)}, ${agoText(fetchedAt, nowMs)}`;
}
